<template>
    <modal size="lg" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" id="addEarningModalNew" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Add Earning Type {{ stepTitleSuffix }}</h3>
        </template>

        <slot>
            <form-errors :errors="formErrors"/>

            <div v-if="dataLoaded">
                <div class="mb-2" v-if="selectedEarningType && step !== 'earning-type'">
                    <div class="p-1 unpad-x bg-150 rounded">
                        <div><span class="text-muted">Base Earning Type: </span>{{ selectedEarningType.name }}</div>
                        <div v-if="step !== 'customize'"><span class="text-muted">Name: </span>{{ formData.name }}</div>
                    </div>
                </div>

                <!-- Step 1: Select base EarningType -->
                <div v-if="step === 'earning-type'">
                    <p>Select the base earning type to use.</p>
                    <fast-table
                        :sections="earningTypeSections"
                        :headers="earningTypeHeaders"
                        :data="earningTypeTableData"
                        @click="onEarningTypeClick"
                        :has-row-radio-select="true"
                        :show-search="true"
                        :show-filter="false"
                        :show-more-actions="false"
                        :has-fixed-sort-order="true"
                    />
                </div>

                <!-- Step 2: Customize -->
                <div v-if="step === 'customize'">
                    <form class="mui-form" @submit.prevent>
                        <div class="form-row" v-if="canCustomize('name')">
                            <div class="col">
                                <form-input v-model="formData.name" :errors="errors.name" type="text" label="Name" />
                            </div>
                        </div>

                        <div class="form-row" v-if="canCustomize('type')">
                            <div class="col">
                                <form-input v-model="formData.type" :errors="errors.type" type="select" label="Type" :options="typeOptions" />
                            </div>
                        </div>

                        <div class="form-row mb-2" v-if="canCustomize('is_work_suffered')">
                            <div class="col">
                                <form-input v-model="formData.is_work_suffered" :errors="errors.is_work_suffered" type="checkbox" label="Is Work Suffered" />
                            </div>
                        </div>

                        <div class="form-row mb-2" v-if="canCustomize('symmetry_wage_type_supplemental_treatment')">
                            <div class="col">
                                <form-input v-model="formData.symmetry_wage_type_supplemental_treatment" :errors="errors.symmetry_wage_type_supplemental_treatment" type="checkbox" label="Supplemental Treatment" />
                            </div>
                        </div>

                        <div class="form-row mb-2" v-if="canCustomize('is_virtual')">
                            <div class="col">
                                <form-input v-model="formData.is_virtual" :errors="errors.is_virtual" type="checkbox" label="Is Virtual" />
                            </div>
                        </div>

                        <div class="form-row mb-2" v-if="canCustomize('is_cash_tips')">
                            <div class="col">
                                <form-input v-model="formData.is_cash_tips" :errors="errors.is_cash_tips" type="checkbox" label="Is Cash Tips" />
                            </div>
                        </div>

                        <template v-if="formData.type == 'hourly' && selectedEarningType?.fields_to_customize?.length">
                            <div class="form-row mb-2">
                                <div class="col">
                                    <form-input v-model="formData.is_tied_to_base_rate" :errors="errors.is_tied_to_base_rate" type="checkbox" label="Is Tied to Base Rate" />
                                </div>
                            </div>

                            <template v-if="formData.is_tied_to_base_rate">
                                <div class="form-row">
                                    <div class="col-6">
                                        <form-input v-model="formData.base_rate_multiplier" :errors="errors.base_rate_multiplier" type="number" label="Base Rate Multiplier" />
                                    </div>
                                </div>

                                <div class="form-row">
                                    <div class="col-6">
                                        <form-input v-model="formData.base_rate_addition" :errors="errors.base_rate_addition" type="number" label="Base Rate Addition" />
                                    </div>
                                </div>
                            </template>
                        </template>
                    </form>
                </div>

                <!-- Step 3: Companies -->
                <div v-if="step === 'companies'">
                    <p class="mb-0">Select the company or companies this earning type will be available for.</p>
                    <company-selector :client="client" :companies="client.companies" v-model="formData.companies" :disabled-row-ids="earningIdCompanyIds[formData.earning_type_id] || []"/>
                </div>

                <!-- Step 4: Positions this earning type is enabled for -->
                <div v-if="step === 'positions' && !editingCompany">
                    <div class="form-row mb-2">
                        <div class="col">
                            <form-input
                                class="mb-2"
                                v-model="formData.default_for_all_positions"
                                :errors="errors.default_for_all_positions"
                                type="checkbox"
                                label="Available to all positions"
                                helper-text="Every position at the selected companies can use this earning type, including positions added later."
                            />
                        </div>
                    </div>

                    <template v-if="!formData.default_for_all_positions">
                    <p>For each company, select which positions can use this earning type.</p>
                    <table class="table css-table widget-table table-fixed mb-2">
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th class="cw-6 text-right">Positions</th>
                            </tr>
                        </thead>
                        <tbody v-for="company in selectedCompanies" :key="company.id">
                            <tr>
                                <td class="truncate-wrapper">
                                    <a href @click.prevent="editCompanyPositions(company)" class="truncate-content">
                                        <span>{{ company.name }}</span>
                                    </a>
                                </td>
                                <td class="text-right">
                                    <span>{{ formData.positions_by_company[company.id] ? formData.positions_by_company[company.id].length : '' }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    </template>
                </div>

                <div v-if="step === 'positions' && editingCompany">
                    <p>Select the positions that can use this earning type for <strong>{{ editingCompany.name }}</strong>.</p>
                    <position-selector :all-positions="companyPositions(editingCompany.id)" :companies="client.companies" v-model="formData.positions_by_company[editingCompany.id]" />
                </div>

                <!-- Step 5: Positions this earning type shows on the paygrid for -->
                <div v-if="step === 'paygrid' && !editingCompany">
                    <!-- An all-positions earning type keeps no per-position records, so
                         its paygrid default is all positions or none. -->
                    <template v-if="formData.default_for_all_positions">
                        <form-input
                            v-model="formData.always_show_on_paygrid"
                            :errors="errors.always_show_on_paygrid"
                            type="checkbox"
                            label="Show on the paygrid by default"
                            helper-text="New jobs at every position are pre-filled with this earning type. Positions cannot be chosen individually while this earning type is available to all of them."
                        />
                    </template>

                    <template v-else>
                    <p>For each company, select which positions show this earning type on the paygrid by default.</p>
                    <table class="table css-table widget-table table-fixed mb-2">
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th class="cw-6 text-right">Positions</th>
                            </tr>
                        </thead>
                        <tbody v-for="company in selectedCompanies" :key="company.id">
                            <tr>
                                <td class="truncate-wrapper">
                                    <a href @click.prevent="editCompanyDefaultPositions(company)" class="truncate-content">
                                        <span>{{ company.name }}</span>
                                    </a>
                                </td>
                                <td class="text-right">
                                    <span>{{ formData.default_positions_by_company[company.id] ? formData.default_positions_by_company[company.id].length : '' }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    </template>
                </div>

                <div v-if="step === 'paygrid' && editingCompany">
                    <p>Select the positions that show this earning type on the paygrid by default for <strong>{{ editingCompany.name }}</strong>.</p>
                    <position-selector :all-positions="selectedCompanyPositions(editingCompany.id)" :companies="client.companies" v-model="formData.default_positions_by_company[editingCompany.id]" :all-positions-label="'Available Positions'" />
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <button-row stacked>
                <button v-if="step === 'earning-type'" type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-else-if="editingCompany" type="button" class="btn btn-outline-primary" @click.prevent="editingCompany = null">
                    Back
                </button>
                <button v-else type="button" class="btn btn-outline-primary" @click.prevent="prevStep">
                    Back
                </button>

                <button v-if="editingCompany" type="button" class="btn btn-primary" @click.prevent="editingCompany = null">
                    Done
                </button>
                <button v-else-if="step === 'paygrid'" type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save &amp; Exit
                </button>
                <button v-else type="button" class="btn btn-primary" @click.prevent="nextStep">
                    Continue
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import FastTable from '@/components/FastTable'
import PositionSelector from '@/pages/client/Settings/components/PositionSelector'
import CompanySelector from '@/pages/client/Settings/components/CompanySelector'

export default {
    props: ['client', 'earningIdCompanyIds', ],
    mixins: [FormMixin, ModalMixin],
    components: {FastTable, PositionSelector, CompanySelector},
    data() {
        return {
            steps: ['earning-type', 'customize', 'companies', 'positions', 'paygrid'],
            step: 'earning-type',
            formData: this.makeFormData(),
            globalEarningTypes: [],
            earningTypesLoaded: false,
            allPositions: [],
            positionsLoaded: false,
            editingCompany: null,
            earningTypeSections: [{title: 'All', id: 'all'}],
            earningTypeHeaders: [
                {label: 'Earning Type', classes: '', defaultSort: false, isFilterable: false, isSearchable: true},
            ],
        }
    },
    mounted() {
        this.getEarningTypes()
        this.getPositions()
    },
    watch: {
        dataLoaded(val) {
            if (val) {
                this.$store.dispatch('STOP_LOADING')
            }
        },
        'formData.companies'(newVal) {
            newVal.forEach(companyId => {
                if (!this.formData.positions_by_company[companyId]) {
                    this.formData.positions_by_company[companyId] = []
                }
                if (!this.formData.default_positions_by_company[companyId]) {
                    this.formData.default_positions_by_company[companyId] = []
                }
            })
        },
    },
    computed: {
        dataLoaded() {
            return this.earningTypesLoaded && this.positionsLoaded
        },
        selectedEarningType() {
            if (!this.formData.earning_type_id) return null
            return this.globalEarningTypes.find(e => e.id === this.formData.earning_type_id) || null
        },
        hasCustomizableFields() {
            return (this.selectedEarningType?.fields_to_customize?.length || 0) > 0
        },
        selectedCompanies() {
            return this.client.companies.filter(c => this.formData.companies.includes(c.id))
        },
        earningTypeTableData() {
            const sorted = [...this.globalEarningTypes].sort((a, b) => a.sort_priority - b.sort_priority)
            return [sorted.map(et => ({
                id: et.id,
                isSelected: et.id === this.formData.earning_type_id,
                object: et,
                cells: [et.name],
            }))]
        },
        typeOptions() {
            return [
                {text: 'Hourly', value: 'hourly'},
                {text: 'Per Unit', value: 'per-unit'},
                {text: 'Amount', value: 'amount'},
            ]
        },
        stepTitleSuffix() {
            if (this.step === 'companies') return '— Companies'
            if (this.step === 'positions') return '— Positions'
            if (this.step === 'paygrid') return '— Paygrid Defaults'
            return ''
        },
    },
    methods: {
        canCustomize(fieldName) {
            return this.selectedEarningType?.fields_to_customize?.includes(fieldName) || false
        },
        companyPositions(companyId) {
            return this.allPositions.filter(p => p.company_id == companyId)
        },
        // The paygrid step can only offer positions the previous step enabled.
        selectedCompanyPositions(companyId) {
            const selected = this.formData.positions_by_company[companyId] || []
            return this.companyPositions(companyId).filter(p => selected.includes(p.id))
        },
        getEarningTypes() {
            this.$store.dispatch('START_LOADING')
            this.$api.get('/admin/tax-engine/earning-types').then(resp => {
                this.globalEarningTypes = resp
                this.earningTypesLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
        getPositions() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/positions`).then(resp => {
                this.allPositions = resp
                this.allPositions.sort((a, b) => a.terminal_value.toLowerCase() > b.terminal_value.toLowerCase() ? 1 : -1)
                this.positionsLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
        makeFormData() {
            return {
                earning_type_id: null,
                name: '',
                type: 'hourly',
                is_work_suffered: false,
                symmetry_wage_type_supplemental_treatment: false,
                is_virtual: false,
                is_cash_tips: false,
                is_tied_to_base_rate: false,
                base_rate_multiplier: '1.00',
                base_rate_addition: '0.00',
                companies: [],
                positions_by_company: {},
                default_positions_by_company: {},
                default_for_all_positions: false,
                always_show_on_paygrid: false,
            }
        },
        onEarningTypeClick(earningType) {
            this.formData.earning_type_id = earningType.id
            this.formData.name = ''
            this.formData.type = earningType.type || 'hourly'
            this.formData.is_work_suffered = earningType.is_work_suffered || false
            this.formData.symmetry_wage_type_supplemental_treatment = earningType.symmetry_wage_type_supplemental_treatment || false
            this.formData.is_virtual = earningType.is_virtual || false
            this.formData.is_cash_tips = earningType.is_cash_tips || false
        },
        editCompanyPositions(company) {
            if (!this.formData.positions_by_company[company.id]) {
                this.formData.positions_by_company[company.id] = []
            }
            this.editingCompany = company
        },
        editCompanyDefaultPositions(company) {
            if (!this.formData.default_positions_by_company[company.id]) {
                this.formData.default_positions_by_company[company.id] = []
            }
            this.editingCompany = company
        },
        // Deselecting a position on the positions step has to drop it from the paygrid
        // step too, or the backend would be asked to default a position the earning
        // type isn't even enabled for.
        pruneDefaultPositions() {
            Object.keys(this.formData.default_positions_by_company).forEach(companyId => {
                const available = this.formData.positions_by_company[companyId] || []
                this.formData.default_positions_by_company[companyId] =
                    this.formData.default_positions_by_company[companyId].filter(pId => available.includes(pId))
            })
        },
        nextStep() {
            if (!this.validateStep()) {
                return
            }

            this.editingCompany = null
            if (this.step === 'positions') {
                this.pruneDefaultPositions()
            }
            let idx = this.steps.indexOf(this.step) + 1
            if (this.steps[idx] === 'customize' && !this.hasCustomizableFields) {
                idx++
            }
            this.step = this.steps[idx]
        },
        prevStep() {
            this.errors = {}
            this.formErrors = []
            this.editingCompany = null
            let idx = this.steps.indexOf(this.step) - 1
            if (this.steps[idx] === 'customize' && !this.hasCustomizableFields) {
                idx--
            }
            this.step = this.steps[idx]
        },
        validateStep() {
            this.errors = {}
            this.formErrors = []

            if (this.step == 'earning-type') {
                if (!this.selectedEarningType) {
                    this.formErrors = ['Select a base earning type to continue.']
                    return false
                }
            }
            if (this.step == 'customize') {
                const errors = {}
                if (this.canCustomize('name') && !this.formData.name.trim()) {
                    errors.name = ['Name is required.']
                }
                if (this.canCustomize('type') && !this.formData.type) {
                    errors.type = ['Type is required.']
                }
                if (this.formData.type == 'hourly' && this.formData.is_tied_to_base_rate) {
                    if (this.formData.base_rate_multiplier === '' || isNaN(Number(this.formData.base_rate_multiplier))) {
                        errors.base_rate_multiplier = ['Enter a number.']
                    }
                    if (this.formData.base_rate_addition === '' || isNaN(Number(this.formData.base_rate_addition))) {
                        errors.base_rate_addition = ['Enter a number.']
                    }
                }
                if (Object.keys(errors).length) {
                    this.errors = errors
                    return false
                }
            }
            if (this.step == 'companies') {
                if (!this.formData.companies.length) {
                    this.formErrors = ['At least one company is required.']
                    return false
                }
            }
            if (this.step == 'positions' && !this.formData.default_for_all_positions) {
                // Every selected company gets its own earning type, and one with no
                // positions would be usable by no one.
                const missing = this.selectedCompanies.filter(c => !this.formData.positions_by_company[c.id]?.length)
                if (missing.length) {
                    this.formErrors = [`Select at least one position for ${missing.map(c => c.name).join(', ')}, or make this earning type available to all positions.`]
                    return false
                }
            }
            //if (this.step == 'paygrid') {
                // NB: no validation necessary here
            //}

            return true
        },
        getFormData() {
            return {
                earning_type_id: this.formData.earning_type_id,
                name: this.formData.name,
                type: this.formData.type,
                is_work_suffered: this.formData.is_work_suffered,
                symmetry_wage_type_supplemental_treatment: this.formData.symmetry_wage_type_supplemental_treatment,
                is_virtual: this.formData.is_virtual,
                is_cash_tips: this.formData.is_cash_tips,
                is_tied_to_base_rate: this.formData.is_tied_to_base_rate,
                base_rate_multiplier: this.formData.base_rate_multiplier,
                base_rate_addition: this.formData.base_rate_addition,
                companies: this.formData.companies,
                positions_by_company: this.formData.positions_by_company,
                // The two paygrid-default levers are mutually exclusive: an
                // all-positions earning type has no per-position records to hang a
                // default on, and an explicitly scoped one has nothing else.
                default_positions_by_company: this.formData.default_for_all_positions ? {} : this.formData.default_positions_by_company,
                default_for_all_positions: this.formData.default_for_all_positions,
                always_show_on_paygrid: this.formData.default_for_all_positions && this.formData.always_show_on_paygrid,
            }
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            this.errors = {}
            this.formErrors = []
            return this.$api.post(`/clients/${this.client.id}/settings/earning-types`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        },
    }
}
</script>
